import type { TFunction } from 'i18next';
import { ApiRequestError, NetworkError } from '../api/client';

/**
 * Turn anything thrown into a message a person can read.
 *
 * The API localises every error it produces, so `error.message` is preferred
 * whenever it exists — it already carries interpolated detail such as the
 * product and warehouse name in an insufficient-stock failure. The i18n
 * lookup is only a fallback for responses that arrived without a body (a
 * proxy timeout, for instance) or for client-side failures.
 */
export function toErrorMessage(error: unknown, t: TFunction): string {
  if (error instanceof ApiRequestError) {
    if (error.message) {
      return error.message;
    }

    const key = `errors.${error.code}`;
    const translated = t(key);
    return translated === key ? t('errors.unexpected') : translated;
  }

  if (error instanceof NetworkError) {
    // Distinguish "the server is down" from "you have no internet": the
    // remedy is different and the wording matters.
    return navigator.onLine ? t('errors.network') : t('errors.offline');
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return t('errors.unexpected');
}

/** A short, translatable title for a failure, based on its HTTP status. */
export function toErrorTitle(error: unknown, t: TFunction): string {
  if (error instanceof NetworkError) {
    return navigator.onLine ? t('errors.network') : t('errors.offline');
  }

  if (error instanceof ApiRequestError) {
    if (error.status === 403) return t('errors.auth.forbidden');
    if (error.status === 404) return t('errors.notFound');
    if (error.status === 429) return t('errors.rateLimited');
    if (error.status >= 500) return t('errors.internal');
  }

  return t('errors.unexpected');
}

/**
 * Field-level messages keyed by field name, for inline form display.
 *
 * Returns an empty object for anything that is not a schema violation, so the
 * caller can always render `errors[field]` without a guard.
 */
export function toFieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiRequestError)) {
    return {};
  }

  const result: Record<string, string> = {};
  for (const issue of error.validationIssues) {
    // Keep the first message per field: showing four messages under one input
    // is noise, and zod reports the most specific failure first.
    if (issue.field && !result[issue.field]) {
      result[issue.field] = issue.message;
    }
  }

  return result;
}
